import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { TrendUp, TrendDown, ArrowLeft, Shield, CaretUp, CaretDown, Funnel, Sun, Moon, Lightning, Info } from "phosphor-react";
import { Button } from "@/components/ui/button";
import GeniLoader from "../components/GeniLoader";

export default function HedgeTradesDetail() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allTrades, setAllTrades] = useState([]);
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  
  // Read setup filter from URL query params
  const setupFilterFromUrl = searchParams.get('setup');
  
  // Filter and sort state
  const [directionFilter, setDirectionFilter] = useState(null);
  const [classificationFilter, setClassificationFilter] = useState(null); // null | 'beneficial' | 'harmful' | 'neutral'
  const [tradeTypeFilter, setTradeTypeFilter] = useState(null); // null | 'day' | 'swing'
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  // Timeframes ordered by duration for classification
  const TIMEFRAME_ORDER = ['1M', '5M', '15M', '30M', '1H', '2H', '4H', '6H', '12H', '1D', '1W'];
  const DAY_TRADE_MAX_INDEX = TIMEFRAME_ORDER.indexOf('2H'); // < 4H = Day Trade

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tradesRes, setupsRes] = await Promise.all([
        api.get('/trades'),
        api.get('/setups')
      ]);
      setAllTrades(tradesRes.data);
      setSetups(setupsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load hedge trades");
    } finally {
      setLoading(false);
    }
  };

  // Get setup name from setup_id
  const getSetupName = (setupId) => {
    if (!setupId) return null;
    const setup = setups.find(s => s.id === setupId);
    return setup?.name || null;
  };

  // Classify trade as Day or Swing based on timeframe
  const getTradeType = (timeframe) => {
    if (!timeframe) return 'day'; // Default to day if no timeframe
    const tfIndex = TIMEFRAME_ORDER.indexOf(timeframe.toUpperCase());
    if (tfIndex === -1) return 'day'; // Unknown timeframe defaults to day
    return tfIndex <= DAY_TRADE_MAX_INDEX ? 'day' : 'swing';
  };

  // Filter hedge trades and compute segmented stats
  const hedgeTradesData = useMemo(() => {
    // First apply setup filter if present
    let tradesToAnalyze = allTrades;
    
    if (setupFilterFromUrl) {
      if (setupFilterFromUrl === '__no_setup__') {
        tradesToAnalyze = allTrades.filter(t => !t.setup_id);
      } else {
        tradesToAnalyze = allTrades.filter(t => t.setup_id === setupFilterFromUrl);
      }
    }
    
    // Filter to only closed trades with hedge
    const hedgedTrades = tradesToAnalyze.filter(t => 
      t.status === 'closed' && 
      t.has_hedge === true &&
      t.pnl_without_hedge !== null &&
      t.pnl_with_hedge !== null
    );

    // Classify by hedge result
    const beneficialTrades = hedgedTrades.filter(t => t.hedge_classification === 'beneficial');
    const harmfulTrades = hedgedTrades.filter(t => t.hedge_classification === 'harmful');
    const neutralTrades = hedgedTrades.filter(t => t.hedge_classification === 'neutral');

    // Calculate totals
    const totalHedgeImpact = hedgedTrades.reduce((sum, t) => sum + (t.hedge_impact || 0), 0);
    const totalWithoutHedge = hedgedTrades.reduce((sum, t) => sum + (t.pnl_without_hedge || 0), 0);
    const totalWithHedge = hedgedTrades.reduce((sum, t) => sum + (t.pnl_with_hedge || 0), 0);

    // TRADE TYPE SEGMENTATION
    const dayTrades = hedgedTrades.filter(t => getTradeType(t.timeframe) === 'day');
    const swingTrades = hedgedTrades.filter(t => getTradeType(t.timeframe) === 'swing');

    // Day Trade Stats
    const dayBeneficial = dayTrades.filter(t => t.hedge_classification === 'beneficial').length;
    const dayHarmful = dayTrades.filter(t => t.hedge_classification === 'harmful').length;
    const dayNeutral = dayTrades.filter(t => t.hedge_classification === 'neutral').length;
    const dayNetImpact = dayTrades.reduce((sum, t) => sum + (t.hedge_impact || 0), 0);
    const dayBeneficialRate = dayTrades.length > 0 ? Math.round((dayBeneficial / dayTrades.length) * 100) : 0;

    // Swing Trade Stats
    const swingBeneficial = swingTrades.filter(t => t.hedge_classification === 'beneficial').length;
    const swingHarmful = swingTrades.filter(t => t.hedge_classification === 'harmful').length;
    const swingNeutral = swingTrades.filter(t => t.hedge_classification === 'neutral').length;
    const swingNetImpact = swingTrades.reduce((sum, t) => sum + (t.hedge_impact || 0), 0);
    const swingBeneficialRate = swingTrades.length > 0 ? Math.round((swingBeneficial / swingTrades.length) * 100) : 0;

    return {
      total: hedgedTrades.length,
      beneficial: beneficialTrades.length,
      harmful: harmfulTrades.length,
      neutral: neutralTrades.length,
      totalHedgeImpact: Math.round(totalHedgeImpact * 100) / 100,
      totalWithoutHedge: Math.round(totalWithoutHedge * 100) / 100,
      totalWithHedge: Math.round(totalWithHedge * 100) / 100,
      trades: hedgedTrades,
      // Day Trade Segment
      dayTrades: {
        total: dayTrades.length,
        beneficial: dayBeneficial,
        harmful: dayHarmful,
        neutral: dayNeutral,
        beneficialRate: dayBeneficialRate,
        netImpact: Math.round(dayNetImpact * 100) / 100
      },
      // Swing Trade Segment
      swingTrades: {
        total: swingTrades.length,
        beneficial: swingBeneficial,
        harmful: swingHarmful,
        neutral: swingNeutral,
        beneficialRate: swingBeneficialRate,
        netImpact: Math.round(swingNetImpact * 100) / 100
      }
    };
  }, [allTrades, setupFilterFromUrl]);

  // Generate Trade Type Insight
  const tradeTypeInsight = useMemo(() => {
    const { dayTrades, swingTrades, total } = hedgeTradesData;
    
    // Need at least 3 trades in each category for meaningful comparison
    if (total < 3) return null;
    
    const hasEnoughDay = dayTrades.total >= 2;
    const hasEnoughSwing = swingTrades.total >= 2;
    
    if (!hasEnoughDay && !hasEnoughSwing) return null;
    
    // Generate insight based on comparison
    if (hasEnoughDay && hasEnoughSwing) {
      const dayPerformsBetter = dayTrades.beneficialRate > swingTrades.beneficialRate;
      const swingPerformsBetter = swingTrades.beneficialRate > dayTrades.beneficialRate;
      const significantDifference = Math.abs(dayTrades.beneficialRate - swingTrades.beneficialRate) >= 15;
      
      // Day positive, Swing negative
      if (dayTrades.netImpact > 0 && swingTrades.netImpact < 0) {
        return {
          type: 'comparison',
          text: `Hedging is beneficial in Day trades (+$${dayTrades.netImpact.toFixed(2)}) but harmful in Swing trades (-$${Math.abs(swingTrades.netImpact).toFixed(2)}). Consider hedging only your intraday positions.`,
          color: '#FFA726',
          icon: 'info'
        };
      }
      
      // Swing positive, Day negative
      if (swingTrades.netImpact > 0 && dayTrades.netImpact < 0) {
        return {
          type: 'comparison',
          text: `Hedging is beneficial in Swing trades (+$${swingTrades.netImpact.toFixed(2)}) but harmful in Day trades (-$${Math.abs(dayTrades.netImpact).toFixed(2)}). Consider hedging only your longer-term positions.`,
          color: '#FFA726',
          icon: 'info'
        };
      }
      
      // Both positive but one significantly better
      if (dayTrades.netImpact > 0 && swingTrades.netImpact > 0 && significantDifference) {
        if (dayPerformsBetter) {
          return {
            type: 'positive',
            text: `Hedging performs better in Day trades (${dayTrades.beneficialRate}% beneficial) than Swing trades (${swingTrades.beneficialRate}% beneficial).`,
            color: '#00E676',
            icon: 'day'
          };
        } else {
          return {
            type: 'positive',
            text: `Hedging performs better in Swing trades (${swingTrades.beneficialRate}% beneficial) than Day trades (${dayTrades.beneficialRate}% beneficial).`,
            color: '#00E676',
            icon: 'swing'
          };
        }
      }
      
      // Both negative
      if (dayTrades.netImpact < 0 && swingTrades.netImpact < 0) {
        return {
          type: 'warning',
          text: `Hedging is showing negative impact in both Day trades (-$${Math.abs(dayTrades.netImpact).toFixed(2)}) and Swing trades (-$${Math.abs(swingTrades.netImpact).toFixed(2)}). Consider reviewing your hedge strategy.`,
          color: '#FF3B30',
          icon: 'warning'
        };
      }
    } else if (hasEnoughDay && !hasEnoughSwing) {
      // Only enough day trade data
      if (dayTrades.netImpact > 0 && dayTrades.beneficialRate >= 50) {
        return {
          type: 'positive',
          text: `Day trades show positive hedge effectiveness (${dayTrades.beneficialRate}% beneficial, +$${dayTrades.netImpact.toFixed(2)} net). Limited swing trade data available.`,
          color: '#00E676',
          icon: 'day'
        };
      } else if (dayTrades.netImpact < 0) {
        return {
          type: 'warning',
          text: `Day trades show low hedge effectiveness (${dayTrades.beneficialRate}% beneficial, -$${Math.abs(dayTrades.netImpact).toFixed(2)} net). Limited swing trade data available.`,
          color: '#FFA726',
          icon: 'day'
        };
      }
    } else if (hasEnoughSwing && !hasEnoughDay) {
      // Only enough swing trade data
      if (swingTrades.netImpact > 0 && swingTrades.beneficialRate >= 50) {
        return {
          type: 'positive',
          text: `Swing trades benefit significantly from hedge usage (${swingTrades.beneficialRate}% beneficial, +$${swingTrades.netImpact.toFixed(2)} net). Limited day trade data available.`,
          color: '#00E676',
          icon: 'swing'
        };
      } else if (swingTrades.netImpact < 0) {
        return {
          type: 'warning',
          text: `Swing trades show negative hedge impact (${swingTrades.beneficialRate}% beneficial, -$${Math.abs(swingTrades.netImpact).toFixed(2)} net). Limited day trade data available.`,
          color: '#FFA726',
          icon: 'swing'
        };
      }
    }
    
    return null;
  }, [hedgeTradesData]);

  // Calculate percentage of all trades
  const percentOfAll = useMemo(() => {
    let totalTradesCount = allTrades.filter(t => t.status === 'closed').length;
    
    if (setupFilterFromUrl) {
      if (setupFilterFromUrl === '__no_setup__') {
        totalTradesCount = allTrades.filter(t => !t.setup_id && t.status === 'closed').length;
      } else {
        totalTradesCount = allTrades.filter(t => t.setup_id === setupFilterFromUrl && t.status === 'closed').length;
      }
    }
    
    if (totalTradesCount === 0) return 0;
    return ((hedgeTradesData.total / totalTradesCount) * 100).toFixed(1);
  }, [allTrades, hedgeTradesData.total, setupFilterFromUrl]);

  // Clear setup filter
  const clearSetupFilter = () => {
    setSearchParams({});
  };

  // Cycle direction filter
  const cycleDirectionFilter = () => {
    if (directionFilter === null) setDirectionFilter('long');
    else if (directionFilter === 'long') setDirectionFilter('short');
    else setDirectionFilter(null);
  };

  // Cycle classification filter
  const cycleClassificationFilter = () => {
    if (classificationFilter === null) setClassificationFilter('beneficial');
    else if (classificationFilter === 'beneficial') setClassificationFilter('harmful');
    else if (classificationFilter === 'harmful') setClassificationFilter('neutral');
    else setClassificationFilter(null);
  };

  // Cycle trade type filter
  const cycleTradeTypeFilter = () => {
    if (tradeTypeFilter === null) setTradeTypeFilter('day');
    else if (tradeTypeFilter === 'day') setTradeTypeFilter('swing');
    else setTradeTypeFilter(null);
  };

  // Cycle Date sort
  const cycleDateSort = () => {
    if (sortColumn !== 'date') {
      setSortColumn('date');
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Cycle Hedge Impact sort
  const cycleHedgeImpactSort = () => {
    if (sortColumn !== 'hedge_impact') {
      setSortColumn('hedge_impact');
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Cycle PnL With Hedge sort
  const cyclePnlWithHedgeSort = () => {
    if (sortColumn !== 'pnl_with_hedge') {
      setSortColumn('pnl_with_hedge');
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Apply filters and sorting
  const filteredAndSortedTrades = useMemo(() => {
    let result = [...hedgeTradesData.trades];

    // Apply direction filter
    if (directionFilter) {
      result = result.filter(t => t.direction === directionFilter);
    }

    // Apply classification filter
    if (classificationFilter) {
      result = result.filter(t => t.hedge_classification === classificationFilter);
    }

    // Apply trade type filter
    if (tradeTypeFilter) {
      result = result.filter(t => getTradeType(t.timeframe) === tradeTypeFilter);
    }

    // Apply sorting
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        if (sortColumn === 'date') {
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
        } else if (sortColumn === 'hedge_impact') {
          aVal = a.hedge_impact ?? 0;
          bVal = b.hedge_impact ?? 0;
        } else if (sortColumn === 'pnl_with_hedge') {
          aVal = a.pnl_with_hedge ?? 0;
          bVal = b.pnl_with_hedge ?? 0;
        }
        
        if (sortDirection === 'desc') {
          return bVal - aVal;
        } else {
          return aVal - bVal;
        }
      });
    } else {
      // Default sort by date descending
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return result;
  }, [hedgeTradesData.trades, directionFilter, classificationFilter, tradeTypeFilter, sortColumn, sortDirection]);

  // Column header component with filter/sort indicator
  const ColumnHeader = ({ label, isActive, activeLabel, onClick, sortDir }) => (
    <th 
      className={`p-3 cursor-pointer select-none transition-colors hover:bg-white/5 ${
        isActive ? 'text-white' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        {isActive && (
          <span className="flex items-center">
            {sortDir === 'desc' && <CaretDown size={12} weight="bold" className="text-cyan-400" />}
            {sortDir === 'asc' && <CaretUp size={12} weight="bold" className="text-cyan-400" />}
            {!sortDir && activeLabel && (
              <span className="text-[10px] px-1 py-0.5 rounded bg-cyan-500/20 text-cyan-400 font-semibold ml-1">
                {activeLabel}
              </span>
            )}
          </span>
        )}
      </div>
    </th>
  );

  // Get classification badge
  const getClassificationBadge = (classification) => {
    switch (classification) {
      case 'beneficial':
        return (
          <span className="px-2 py-0.5 text-xs font-mono rounded bg-[#00E676]/10 text-[#00E676]">
            Beneficial
          </span>
        );
      case 'harmful':
        return (
          <span className="px-2 py-0.5 text-xs font-mono rounded bg-[#FF3B30]/10 text-[#FF3B30]">
            Harmful
          </span>
        );
      case 'neutral':
        return (
          <span className="px-2 py-0.5 text-xs font-mono rounded bg-white/10 text-white/60">
            Neutral
          </span>
        );
      default:
        return <span className="text-white/40">-</span>;
    }
  };

  // Get trade type badge
  const getTradeTypeBadge = (timeframe) => {
    const type = getTradeType(timeframe);
    if (type === 'swing') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">
          <Moon size={10} weight="fill" />
          Swing
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
        <Sun size={10} weight="fill" />
        Day
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="hedge-trades-detail-page">
      <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/advanced')}
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/5 p-2"
            data-testid="back-to-advanced"
          >
            <ArrowLeft size={20} weight="bold" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Shield size={28} weight="duotone" className="text-cyan-400" />
              <div>
                <div className="text-white/40 text-xs font-mono uppercase tracking-wider">ANALYTICS</div>
                <h2 className="text-3xl font-black font-heading tracking-tight" data-testid="page-title">
                  Hedge Trades
                </h2>
              </div>
            </div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm px-4 py-2">
            <span className="text-white/60 text-sm font-mono">TOTAL: </span>
            <span className="text-cyan-400 font-bold text-lg" data-testid="total-count">{hedgeTradesData.total}</span>
          </div>
        </div>

        {/* Setup Filter Indicator */}
        {setupFilterFromUrl && (
          <div className="mb-4 flex items-center gap-2 text-white/60 text-sm" data-testid="setup-filter-indicator">
            <span>Showing hedge trades for:</span>
            <span className="font-bold text-cyan-400">
              {setupFilterFromUrl === '__no_setup__' ? 'No setup' : (getSetupName(setupFilterFromUrl) || setupFilterFromUrl)}
            </span>
            <button
              onClick={clearSetupFilter}
              className="text-white/40 hover:text-white/60 ml-1 underline"
              data-testid="clear-setup-filter"
            >
              (clear)
            </button>
          </div>
        )}

        {/* Performance Summary Row */}
        {hedgeTradesData.total > 0 && (
          <div className="mb-6" data-testid="hedge-trades-summary">
            <div className="text-white/40 text-xs font-mono uppercase tracking-wider mb-3">
              Summary
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {/* Total Trades */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">TRADES</div>
                  <div className="text-xl font-bold font-mono text-cyan-400" data-testid="summary-total">
                    {hedgeTradesData.total}
                  </div>
                </div>
                
                {/* % of Closed Trades */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">% OF CLOSED</div>
                  <div className="text-xl font-bold font-mono text-white" data-testid="summary-percent">
                    {percentOfAll}%
                  </div>
                </div>
                
                {/* Beneficial */}
                <div className="bg-[#000000] border border-[#00E676]/20 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">BENEFICIAL</div>
                  <div className="text-xl font-bold font-mono text-[#00E676]" data-testid="summary-beneficial">
                    {hedgeTradesData.beneficial}
                  </div>
                </div>
                
                {/* Harmful */}
                <div className="bg-[#000000] border border-[#FF3B30]/20 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">HARMFUL</div>
                  <div className="text-xl font-bold font-mono text-[#FF3B30]" data-testid="summary-harmful">
                    {hedgeTradesData.harmful}
                  </div>
                </div>
                
                {/* Net Impact */}
                <div className="bg-[#000000] border border-cyan-500/20 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">NET IMPACT</div>
                  <div className={`text-xl font-bold font-mono ${
                    hedgeTradesData.totalHedgeImpact > 0 ? 'text-[#00E676]' :
                    hedgeTradesData.totalHedgeImpact < 0 ? 'text-[#FF3B30]' :
                    'text-white/60'
                  }`} data-testid="summary-net-impact">
                    {hedgeTradesData.totalHedgeImpact >= 0 ? '+' : ''}${hedgeTradesData.totalHedgeImpact.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TRADE TYPE SEGMENTATION */}
        {hedgeTradesData.total > 0 && (
          <div className="mb-6" data-testid="trade-type-segmentation">
            <div className="text-white/40 text-xs font-mono uppercase tracking-wider mb-3">
              Performance by Trade Type
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Day Trade Performance */}
              <div className="bg-[#121212] border border-blue-500/20 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sun size={18} weight="duotone" className="text-blue-400" />
                  <span className="text-blue-400 text-sm font-bold">Day Trade Hedge Performance</span>
                </div>
                {hedgeTradesData.dayTrades.total === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-white/40 text-sm">No hedge data for Day Trades yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">TRADES</div>
                        <div className="text-lg font-bold font-mono text-white">{hedgeTradesData.dayTrades.total}</div>
                      </div>
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">BENEFICIAL</div>
                        <div className="text-lg font-bold font-mono text-[#00E676]">{hedgeTradesData.dayTrades.beneficial}</div>
                      </div>
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">HARMFUL</div>
                        <div className="text-lg font-bold font-mono text-[#FF3B30]">{hedgeTradesData.dayTrades.harmful}</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-white/10">
                      <div>
                        <span className="text-white/40 text-xs">Beneficial Rate: </span>
                        <span className={`font-mono font-bold ${
                          hedgeTradesData.dayTrades.beneficialRate >= 50 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                        }`}>{hedgeTradesData.dayTrades.beneficialRate}%</span>
                      </div>
                      <div>
                        <span className="text-white/40 text-xs">Net: </span>
                        <span className={`font-mono font-bold ${
                          hedgeTradesData.dayTrades.netImpact > 0 ? 'text-[#00E676]' :
                          hedgeTradesData.dayTrades.netImpact < 0 ? 'text-[#FF3B30]' :
                          'text-white/60'
                        }`}>
                          {hedgeTradesData.dayTrades.netImpact >= 0 ? '+' : ''}${hedgeTradesData.dayTrades.netImpact.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Swing Trade Performance */}
              <div className="bg-[#121212] border border-purple-500/20 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Moon size={18} weight="duotone" className="text-purple-400" />
                  <span className="text-purple-400 text-sm font-bold">Swing Trade Hedge Performance</span>
                </div>
                {hedgeTradesData.swingTrades.total === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-white/40 text-sm">No hedge data for Swing Trades yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">TRADES</div>
                        <div className="text-lg font-bold font-mono text-white">{hedgeTradesData.swingTrades.total}</div>
                      </div>
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">BENEFICIAL</div>
                        <div className="text-lg font-bold font-mono text-[#00E676]">{hedgeTradesData.swingTrades.beneficial}</div>
                      </div>
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">HARMFUL</div>
                        <div className="text-lg font-bold font-mono text-[#FF3B30]">{hedgeTradesData.swingTrades.harmful}</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-white/10">
                      <div>
                        <span className="text-white/40 text-xs">Beneficial Rate: </span>
                        <span className={`font-mono font-bold ${
                          hedgeTradesData.swingTrades.beneficialRate >= 50 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                        }`}>{hedgeTradesData.swingTrades.beneficialRate}%</span>
                      </div>
                      <div>
                        <span className="text-white/40 text-xs">Net: </span>
                        <span className={`font-mono font-bold ${
                          hedgeTradesData.swingTrades.netImpact > 0 ? 'text-[#00E676]' :
                          hedgeTradesData.swingTrades.netImpact < 0 ? 'text-[#FF3B30]' :
                          'text-white/60'
                        }`}>
                          {hedgeTradesData.swingTrades.netImpact >= 0 ? '+' : ''}${hedgeTradesData.swingTrades.netImpact.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Trade Type Insight */}
            {tradeTypeInsight && (
              <div 
                className="mt-4 p-4 rounded-sm border flex items-start gap-3"
                style={{
                  backgroundColor: `${tradeTypeInsight.color}10`,
                  borderColor: `${tradeTypeInsight.color}30`
                }}
                data-testid="trade-type-insight"
              >
                {tradeTypeInsight.icon === 'day' && <Sun size={20} weight="duotone" style={{ color: tradeTypeInsight.color }} />}
                {tradeTypeInsight.icon === 'swing' && <Moon size={20} weight="duotone" style={{ color: tradeTypeInsight.color }} />}
                {tradeTypeInsight.icon === 'info' && <Info size={20} weight="duotone" style={{ color: tradeTypeInsight.color }} />}
                {tradeTypeInsight.icon === 'warning' && <Lightning size={20} weight="duotone" style={{ color: tradeTypeInsight.color }} />}
                <div>
                  <div className="text-white/40 text-[10px] font-mono mb-1">TRADE TYPE INSIGHT</div>
                  <p className="text-sm" style={{ color: tradeTypeInsight.color }}>
                    {tradeTypeInsight.text}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        {hedgeTradesData.total === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <div className="w-16 h-16 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield size={32} weight="duotone" className="text-cyan-400/60" />
            </div>
            <h3 className="text-xl font-bold font-heading mb-2">
              {setupFilterFromUrl ? "No Hedge Trades for This Setup" : "No Hedge Trades Found"}
            </h3>
            <p className="text-white/60 mb-4">
              {setupFilterFromUrl 
                ? "No trades with hedges recorded for this setup."
                : "Trades with hedges will appear here after they close."}
            </p>
            {setupFilterFromUrl && (
              <Button
                onClick={clearSetupFilter}
                variant="outline"
                className="border-white/10 text-white/70 hover:bg-white/5"
                data-testid="clear-filter-empty-state"
              >
                View All Hedge Trades
              </Button>
            )}
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Trade List */}
            <div className="flex-1">
              {/* Active Filters Summary */}
              {(directionFilter || classificationFilter || tradeTypeFilter || sortColumn) && (
                <div className="mb-3 flex items-center gap-2 text-xs">
                  <Funnel size={14} className="text-white/40" />
                  <span className="text-white/40">Active:</span>
                  {directionFilter && (
                    <span className="px-2 py-0.5 rounded bg-white/10 text-white/80">
                      {directionFilter.toUpperCase()}
                    </span>
                  )}
                  {tradeTypeFilter && (
                    <span className={`px-2 py-0.5 rounded ${
                      tradeTypeFilter === 'day' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {tradeTypeFilter === 'day' ? 'Day' : 'Swing'}
                    </span>
                  )}
                  {classificationFilter && (
                    <span className={`px-2 py-0.5 rounded ${
                      classificationFilter === 'beneficial' ? 'bg-[#00E676]/20 text-[#00E676]' : 
                      classificationFilter === 'harmful' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                      'bg-white/10 text-white/60'
                    }`}>
                      {classificationFilter.charAt(0).toUpperCase() + classificationFilter.slice(1)}
                    </span>
                  )}
                  {sortColumn && (
                    <span className="px-2 py-0.5 rounded bg-white/10 text-white/80">
                      {sortColumn === 'hedge_impact' ? 'IMPACT' : sortColumn === 'pnl_with_hedge' ? 'PNL' : sortColumn.toUpperCase()} {sortDirection === 'desc' ? '↓' : '↑'}
                    </span>
                  )}
                  <button 
                    onClick={() => {
                      setDirectionFilter(null);
                      setClassificationFilter(null);
                      setTradeTypeFilter(null);
                      setSortColumn(null);
                      setSortDirection(null);
                    }}
                    className="text-white/40 hover:text-white ml-2 underline"
                    data-testid="clear-all-filters"
                  >
                    Clear all
                  </button>
                </div>
              )}
              
              <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                <table className="w-full" data-testid="hedge-trades-table">
                  <thead className="bg-[#0A0A0A]">
                    <tr className="text-left text-xs text-white/40 font-mono">
                      <th className="p-3">ASSET</th>
                      <ColumnHeader 
                        label="DIRECTION" 
                        isActive={!!directionFilter}
                        activeLabel={directionFilter?.toUpperCase()}
                        onClick={cycleDirectionFilter}
                      />
                      <ColumnHeader 
                        label="TYPE" 
                        isActive={!!tradeTypeFilter}
                        activeLabel={tradeTypeFilter === 'day' ? 'DAY' : tradeTypeFilter === 'swing' ? 'SWING' : null}
                        onClick={cycleTradeTypeFilter}
                      />
                      <th className="p-3">SETUP</th>
                      <ColumnHeader 
                        label="HEDGE IMPACT" 
                        isActive={sortColumn === 'hedge_impact'}
                        sortDir={sortColumn === 'hedge_impact' ? sortDirection : null}
                        onClick={cycleHedgeImpactSort}
                      />
                      <th className="p-3">PNL W/O HEDGE</th>
                      <ColumnHeader 
                        label="PNL WITH HEDGE" 
                        isActive={sortColumn === 'pnl_with_hedge'}
                        sortDir={sortColumn === 'pnl_with_hedge' ? sortDirection : null}
                        onClick={cyclePnlWithHedgeSort}
                      />
                      <ColumnHeader 
                        label="OUTCOME" 
                        isActive={!!classificationFilter}
                        activeLabel={classificationFilter ? classificationFilter.toUpperCase() : null}
                        onClick={cycleClassificationFilter}
                      />
                      <ColumnHeader 
                        label="DATE" 
                        isActive={sortColumn === 'date'}
                        sortDir={sortColumn === 'date' ? sortDirection : null}
                        onClick={cycleDateSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedTrades.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="p-8 text-center text-white/40">
                          No trades match current filters
                        </td>
                      </tr>
                    ) : (
                      filteredAndSortedTrades.map((trade) => (
                        <tr
                          key={trade.id}
                          data-testid={`trade-row-${trade.id}`}
                          onClick={() => setSelectedTrade(trade)}
                          className={`trade-row border-t border-white/10 cursor-pointer hover:bg-white/5 ${
                            selectedTrade?.id === trade.id ? "bg-white/5" : ""
                          }`}
                        >
                          <td className="p-3">
                            <div className="font-mono text-white font-bold">{trade.asset}</div>
                            <div className="text-xs text-white/40">{trade.timeframe || '-'}</div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {trade.direction === "long" ? (
                                <TrendUp size={16} weight="bold" className="text-[#00E676]" />
                              ) : (
                                <TrendDown size={16} weight="bold" className="text-[#FF3B30]" />
                              )}
                              <span
                                className={`font-mono text-sm uppercase ${
                                  trade.direction === "long" ? "text-[#00E676]" : "text-[#FF3B30]"
                                }`}
                              >
                                {trade.direction}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            {getTradeTypeBadge(trade.timeframe)}
                          </td>
                          <td className="p-3">
                            <span className="text-white/60 text-sm">
                              {trade.setup_name || '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`font-mono text-sm font-bold ${
                              (trade.hedge_impact ?? 0) > 0 ? 'text-cyan-400' : 
                              (trade.hedge_impact ?? 0) < 0 ? 'text-cyan-400' : 'text-white/60'
                            }`}>
                              {trade.hedge_impact != null ? `${trade.hedge_impact >= 0 ? '+' : ''}$${trade.hedge_impact.toFixed(2)}` : '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`font-mono text-sm ${
                              (trade.pnl_without_hedge ?? 0) >= 0 ? 'text-white/60' : 'text-white/60'
                            }`}>
                              {trade.pnl_without_hedge != null ? `${trade.pnl_without_hedge >= 0 ? '+' : ''}$${trade.pnl_without_hedge.toFixed(2)}` : '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`font-mono text-sm font-semibold ${
                              (trade.pnl_with_hedge ?? 0) > 0 ? 'text-[#00E676]' : 
                              (trade.pnl_with_hedge ?? 0) < 0 ? 'text-[#FF3B30]' : 'text-white/60'
                            }`}>
                              {trade.pnl_with_hedge != null ? `${trade.pnl_with_hedge >= 0 ? '+' : ''}$${trade.pnl_with_hedge.toFixed(2)}` : '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            {getClassificationBadge(trade.hedge_classification)}
                          </td>
                          <td className="p-3">
                            <span className="text-white/60 text-sm">
                              {trade.created_at && format(new Date(trade.created_at), "MMM d, yyyy")}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                
                {/* Results count */}
                {(directionFilter || classificationFilter || tradeTypeFilter) && (
                  <div className="p-3 border-t border-white/10 text-xs text-white/40">
                    Showing {filteredAndSortedTrades.length} of {hedgeTradesData.total} trades
                  </div>
                )}
              </div>
            </div>

            {/* Trade Details Panel */}
            {selectedTrade && (
              <div className="w-[420px] shrink-0">
                <div className="bg-[#121212] border border-white/10 rounded-sm p-6 sticky top-8" data-testid="trade-details-panel">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold font-heading">Trade Details</h3>
                    <Button
                      onClick={() => setSelectedTrade(null)}
                      variant="ghost"
                      size="sm"
                      className="text-white/40 hover:text-white hover:bg-white/5"
                      data-testid="close-details-panel"
                    >
                      <ArrowLeft size={16} />
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {/* Chart Image */}
                    {selectedTrade.image_data && (
                      <div className="bg-[#000000] border border-white/10 rounded-sm overflow-hidden">
                        <img 
                          src={`data:image/png;base64,${selectedTrade.image_data}`}
                          alt="Trade chart"
                          className="w-full h-auto object-contain"
                          data-testid="trade-chart-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Asset & Direction */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-black font-heading">{selectedTrade.asset}</div>
                        <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-bold ${
                          selectedTrade.direction === "long" 
                            ? "bg-[#00E676]/10 text-[#00E676]" 
                            : "bg-[#FF3B30]/10 text-[#FF3B30]"
                        }`}>
                          {selectedTrade.direction === "long" ? (
                            <TrendUp size={14} weight="bold" />
                          ) : (
                            <TrendDown size={14} weight="bold" />
                          )}
                          {selectedTrade.direction?.toUpperCase()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white/40 mb-1">HEDGE RESULT</div>
                        <div className={`px-2 py-1 rounded text-sm font-bold ${
                          selectedTrade.hedge_classification === 'beneficial'
                            ? 'bg-[#00E676]/10 text-[#00E676]'
                            : selectedTrade.hedge_classification === 'harmful'
                            ? 'bg-[#FF3B30]/10 text-[#FF3B30]'
                            : 'bg-white/10 text-white/60'
                        }`}>
                          {selectedTrade.hedge_classification?.toUpperCase() || 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* HEDGE DETAILS SECTION */}
                    <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-sm p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Shield size={16} weight="duotone" className="text-cyan-400" />
                        <span className="text-cyan-400/80 text-xs font-mono uppercase">Hedge Details</span>
                      </div>
                      
                      {/* Key Metrics */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <span className="text-white/40 text-xs">Total Hedge Value</span>
                          <div className="font-mono font-bold text-cyan-400 text-lg">
                            {(selectedTrade.hedge_value || 0) >= 0 ? '+' : ''}${(selectedTrade.hedge_value || 0).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <span className="text-white/40 text-xs">Hedge Count</span>
                          <div className="font-mono font-bold text-white text-lg">
                            {selectedTrade.hedge_count || 1}
                          </div>
                        </div>
                      </div>
                      
                      {/* PnL Comparison */}
                      <div className="bg-[#000000] rounded-sm p-3 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">PnL Without Hedge</span>
                          <span className="font-mono text-white/60">
                            {(selectedTrade.pnl_without_hedge || 0) >= 0 ? '+' : ''}${(selectedTrade.pnl_without_hedge || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">PnL With Hedge</span>
                          <span className={`font-mono font-bold ${
                            (selectedTrade.pnl_with_hedge || 0) > 0 ? 'text-[#00E676]' :
                            (selectedTrade.pnl_with_hedge || 0) < 0 ? 'text-[#FF3B30]' :
                            'text-white'
                          }`}>
                            {(selectedTrade.pnl_with_hedge || 0) >= 0 ? '+' : ''}${(selectedTrade.pnl_with_hedge || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="pt-2 border-t border-white/10 flex justify-between text-sm">
                          <span className="text-cyan-400/70">Hedge Impact</span>
                          <span className={`font-mono font-bold ${
                            (selectedTrade.hedge_impact || 0) > 0 ? 'text-[#00E676]' :
                            (selectedTrade.hedge_impact || 0) < 0 ? 'text-[#FF3B30]' :
                            'text-white/60'
                          }`}>
                            {(selectedTrade.hedge_impact || 0) >= 0 ? '+' : ''}${(selectedTrade.hedge_impact || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Hedge Entries */}
                      {selectedTrade.hedge_entries && selectedTrade.hedge_entries.length > 0 && (
                        <div className="mt-4">
                          <div className="text-white/40 text-xs font-mono mb-2">HEDGE ENTRIES</div>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {selectedTrade.hedge_entries.map((entry, idx) => (
                              <div 
                                key={entry.id || idx} 
                                className="bg-[#000000] rounded px-3 py-2 text-sm"
                              >
                                <div className="flex justify-between items-center">
                                  <span className={`font-mono font-semibold ${
                                    (entry.value || 0) >= 0 ? 'text-cyan-400' : 'text-cyan-400'
                                  }`}>
                                    {(entry.value || 0) >= 0 ? '+' : ''}${(entry.value || 0).toFixed(2)}
                                  </span>
                                  {entry.recorded_at && (
                                    <span className="text-white/30 text-xs">
                                      {format(new Date(entry.recorded_at), "MMM d, HH:mm")}
                                    </span>
                                  )}
                                </div>
                                {entry.note && (
                                  <div className="text-white/50 text-xs mt-1 truncate" title={entry.note}>
                                    {entry.note}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Price Levels */}
                    <div className="grid grid-cols-2 gap-4">
                      {selectedTrade.entry && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">ENTRY</div>
                          <div className="text-white font-mono font-semibold">
                            ${selectedTrade.entry?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedTrade.closing_price && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">EXIT</div>
                          <div className={`font-mono font-semibold ${
                            selectedTrade.outcome === 'win' ? 'text-[#00E676]' : 'text-[#FF3B30]'
                          }`}>
                            ${selectedTrade.closing_price?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedTrade.stop_loss && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">STOP LOSS</div>
                          <div className="text-[#FF3B30] font-mono font-semibold">
                            ${selectedTrade.stop_loss?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedTrade.take_profit && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">TAKE PROFIT</div>
                          <div className="text-[#00E676] font-mono font-semibold">
                            ${selectedTrade.take_profit?.toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Additional Info */}
                    <div className="space-y-3 pt-4 border-t border-white/10">
                      {selectedTrade.rr != null && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">RR (With Hedge)</span>
                          <span className={`font-mono ${
                            selectedTrade.rr >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                          }`}>
                            {selectedTrade.rr.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {selectedTrade.rr_without_hedge != null && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">RR (Without Hedge)</span>
                          <span className="font-mono text-white/60">
                            {selectedTrade.rr_without_hedge.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {selectedTrade.timeframe && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Timeframe</span>
                          <span className="text-white font-mono">{selectedTrade.timeframe}</span>
                        </div>
                      )}
                      {selectedTrade.setup_name && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Setup</span>
                          <span className="text-white font-medium">{selectedTrade.setup_name}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-white/40">Closed</span>
                        <span className="text-white/60">
                          {selectedTrade.closed_at && format(new Date(selectedTrade.closed_at), "MMM d, yyyy HH:mm")}
                        </span>
                      </div>
                    </div>

                    {/* Notes */}
                    {selectedTrade.notes && (
                      <div className="pt-4 border-t border-white/10">
                        <div className="text-white/40 text-xs font-mono mb-2">NOTES</div>
                        <p className="text-white/80 text-sm whitespace-pre-wrap">{selectedTrade.notes}</p>
                      </div>
                    )}

                    {/* Read-only notice */}
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-white/30 text-xs text-center">
                        This is a read-only view. To edit trades, go to Trade History.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
